import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, Crown, Sparkles, ArrowUpRight, GraduationCap } from "lucide-react";

/**
 * GoldenPassBadge — gold "GOLDEN PASS ACTIVE" pill with countdown.
 *
 * Render anywhere a logged-in user is in scope. Self-fetches from
 * `/api/war-college/me`. When the user has ≤15 days remaining, surfaces
 * the conversion CTA (Day-45 protocol).
 *
 * Render variants:
 *   - "pill" (default): compact gold badge with a countdown for headers/nav.
 *   - "card": full panel with countdown + niches + conversion CTA.
 */
export default function GoldenPassBadge({ variant = "card" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/war-college/me");
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (err || !data || !data.active) return null;

  const days = data.days_remaining;
  const hours = data.hours_remaining;
  const showDayCount = days >= 1;
  const isUrgent = data.conversion_notice;

  if (variant === "pill") {
    return (
      <span
        data-testid="golden-pass-pill"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider border ${
          isUrgent
            ? "border-amber-400/60 bg-amber-400/15 text-amber-200"
            : "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#F5D87E]"
        }`}
      >
        <Crown className="w-3 h-3" />
        GOLDEN PASS · {showDayCount ? `${days}d` : `${hours}h`}
      </span>
    );
  }

  return (
    <div
      data-testid="golden-pass-card"
      className="glass-strong rounded-xl p-6 mb-8 border border-[#D4AF37]/30 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 80% 30%, rgba(245,216,126,0.55), transparent 60%)," +
            "radial-gradient(ellipse at 10% 90%, rgba(212,175,55,0.35), transparent 60%)",
        }}
      />
      <div className="relative flex flex-wrap items-start justify-between gap-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-5 h-5 text-[#F5D87E]" />
            <div className="text-[10px] tracking-armada text-[#F5D87E] font-bold">
              GOLDEN PASS · ACTIVE
            </div>
            <span className="text-[9px] tracking-wider text-[#D4AF37] px-1.5 py-0.5 rounded border border-[#D4AF37]/40 bg-[#D4AF37]/10 uppercase ml-1">
              {data.tier || "master"}
            </span>
          </div>
          <h3 className="text-2xl md:text-3xl font-black tracking-tighter text-white">
            War College · All Niches Unlocked
          </h3>
          <div className="flex flex-wrap gap-2 mt-3">
            {(data.niches || []).map((n) => (
              <span
                key={n}
                data-testid={`golden-pass-niche-${n.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[10px] tracking-wider text-zinc-300 px-2 py-0.5 rounded bg-black/40 border border-white/10"
              >
                <Sparkles className="inline w-3 h-3 mr-1 text-[#D4AF37]" />
                {n}
              </span>
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-armada text-zinc-500 mb-1">REMAINING</div>
          <div
            data-testid="golden-pass-countdown"
            className="text-5xl md:text-6xl font-black text-white tracking-tighter tabular-nums"
          >
            {showDayCount ? days : hours}
            <span className="text-2xl text-zinc-500 font-medium ml-1">
              {showDayCount ? (days === 1 ? "day" : "days") : (hours === 1 ? "hr" : "hrs")}
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            expires {new Date(data.expires_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {isUrgent && (
        <div
          data-testid="golden-pass-conversion-cta"
          className="relative mt-6 p-4 rounded-lg border border-amber-400/50 bg-amber-400/10"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-[10px] tracking-armada text-amber-300 font-bold mb-1">
                CONVERT TO MAINTAIN ACCESS · DAY {60 - days} OF 60
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed mb-3">
                {data.subscription_cta}
              </p>
              <button
                data-testid="golden-pass-upgrade-btn"
                onClick={() => {
                  // TODO: wire to Stripe Checkout subscription session
                  window.dispatchEvent(
                    new CustomEvent("goldenpass:upgrade", { detail: data })
                  );
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gradient-to-r from-amber-400 to-[#D4AF37] text-black text-xs font-bold tracking-wider uppercase hover:from-amber-300 hover:to-[#F5D87E] transition"
              >
                <GraduationCap className="w-4 h-4" />
                Upgrade · ${data.subscription_usd_per_month}/mo
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
